import { useCallback, useEffect, useState } from "react";
import {
  DEFAULT_AGENCY_STUDIO_INTEGRATIONS,
  type AgencyStudioOnboardingUpsertV1,
  type AgencyStudioOnboardingViewV1,
} from "@enrein/shared-contracts";
import type { StudioApiClient } from "../../../api.js";
import { V10Kicker } from "../components/V10Primitives.js";

const EMPTY_FORM: AgencyStudioOnboardingUpsertV1 = {
  schemaVersion: "agency-studio-onboarding-upsert.v1.0",
  displayName: "",
  brandPrimaryColor: "#0F3D2E",
  ownerDisplayName: "",
  teamMembers: [],
  integrations: DEFAULT_AGENCY_STUDIO_INTEGRATIONS,
  onboardingWizardCompleted: false,
};

function viewToForm(view: AgencyStudioOnboardingViewV1): AgencyStudioOnboardingUpsertV1 {
  return {
    schemaVersion: "agency-studio-onboarding-upsert.v1.0",
    displayName: view.displayName,
    brandPrimaryColor: view.brandPrimaryColor,
    ownerDisplayName: view.ownerDisplayName,
    teamMembers: [...view.teamMembers],
    integrations: [...view.integrations],
    onboardingWizardCompleted: view.onboardingWizardCompleted,
  };
}

export function V10SettingsView({
  deployEnv,
  agencyId,
  api,
}: {
  readonly deployEnv: string;
  readonly agencyId?: string;
  readonly api: Pick<StudioApiClient, "getAgencyStudioOnboarding" | "upsertAgencyStudioOnboarding">;
}) {
  const [form, setForm] = useState<AgencyStudioOnboardingUpsertV1>(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void api
      .getAgencyStudioOnboarding()
      .then((view) => {
        if (!cancelled) setForm(viewToForm(view));
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load settings");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  const onSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const view = await api.upsertAgencyStudioOnboarding(form);
      setForm(viewToForm(view));
      setSavedAt(new Date().toLocaleTimeString());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to save settings");
    } finally {
      setSaving(false);
    }
  }, [api, form]);

  const updateIntegration = (integrationId: AgencyStudioOnboardingUpsertV1["integrations"][number]["integrationId"], status: "NOT_CONFIGURED" | "CONFIGURED" | "DEFERRED") => {
    setForm((prev) => ({
      ...prev,
      integrations: prev.integrations.map((row) =>
        row.integrationId === integrationId ? { ...row, status } : row,
      ),
    }));
  };

  return (
    <section className="v10-screen" aria-label="Settings">
      <div className="v10-section-head v10-section-head--flush">
        <div>
          <V10Kicker>Settings</V10Kicker>
          <h2>Agency identity, brand, team, and integrations.</h2>
          <p className="v10-muted">
            Agency: <strong>{agencyId ?? "Connected agency"}</strong> · Environment: <strong>{deployEnv}</strong>
          </p>
        </div>
        <button type="button" className="v10-btn v10-btn--primary" disabled={loading || saving} onClick={() => void onSave()}>
          {saving ? "Saving…" : "Save configuration"}
        </button>
      </div>
      {error ? <p className="v10-error" role="alert">{error}</p> : null}
      {savedAt ? <p className="v10-muted">Saved at {savedAt}.</p> : null}
      {loading ? <p className="v10-muted">Loading agency settings…</p> : null}
      <div className="v10-grid-two">
        <article className="v10-card v10-card--major">
          <h3>Agency identity</h3>
          <label className="v10-field">
            <span>Display name</span>
            <input
              value={form.displayName}
              onChange={(e) => setForm((prev) => ({ ...prev, displayName: e.target.value }))}
              disabled={loading}
            />
          </label>
          <label className="v10-field">
            <span>Owner display name</span>
            <input
              value={form.ownerDisplayName}
              onChange={(e) => setForm((prev) => ({ ...prev, ownerDisplayName: e.target.value }))}
              disabled={loading}
            />
          </label>
        </article>
        <article className="v10-card v10-card--major">
          <h3>Brand</h3>
          <label className="v10-field">
            <span>Primary color</span>
            <input
              type="color"
              value={form.brandPrimaryColor}
              onChange={(e) => setForm((prev) => ({ ...prev, brandPrimaryColor: e.target.value }))}
              disabled={loading}
            />
          </label>
          <p className="v10-muted">Token: {form.brandPrimaryColor}</p>
        </article>
        <article className="v10-card v10-card--major">
          <h3>Team</h3>
          {form.teamMembers.map((member, index) => (
            <div key={`${member.email}-${index}`} className="v10-inline-fields">
              <input
                aria-label={`Team member ${index + 1} email`}
                value={member.email}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    teamMembers: prev.teamMembers.map((row, i) =>
                      i === index ? { ...row, email: e.target.value } : row,
                    ),
                  }))
                }
                disabled={loading}
              />
              <input
                aria-label={`Team member ${index + 1} role`}
                value={member.roleLabel}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    teamMembers: prev.teamMembers.map((row, i) =>
                      i === index ? { ...row, roleLabel: e.target.value } : row,
                    ),
                  }))
                }
                disabled={loading}
              />
            </div>
          ))}
          <button
            type="button"
            className="v10-btn v10-btn--ghost"
            disabled={loading}
            onClick={() =>
              setForm((prev) => ({
                ...prev,
                teamMembers: [...prev.teamMembers, { email: "teammate@agency.test", roleLabel: "Operator" }],
              }))
            }
          >
            Add team member
          </button>
        </article>
        <article className="v10-card v10-card--major">
          <h3>Required integrations</h3>
          {form.integrations.map((row) => (
            <label key={row.integrationId} className="v10-field">
              <span>{row.integrationId.replaceAll("_", " ")}</span>
              <select
                value={row.status}
                onChange={(e) =>
                  updateIntegration(
                    row.integrationId,
                    e.target.value as "NOT_CONFIGURED" | "CONFIGURED" | "DEFERRED",
                  )
                }
                disabled={loading}
              >
                <option value="NOT_CONFIGURED">Not configured</option>
                <option value="CONFIGURED">Configured</option>
                <option value="DEFERRED">Deferred</option>
              </select>
            </label>
          ))}
          <label className="v10-field v10-checkbox">
            <input
              type="checkbox"
              checked={form.onboardingWizardCompleted}
              onChange={(e) => setForm((prev) => ({ ...prev, onboardingWizardCompleted: e.target.checked }))}
              disabled={loading}
            />
            <span>Onboarding wizard completed</span>
          </label>
        </article>
        <article className="v10-card v10-card--major v10-card--green">
          <h3>System rules</h3>
          <p>BusinessGraph remains website truth.</p>
          <p>ClientOps and Analytics do not mutate website truth.</p>
          <p>No ranking guarantees or launch certification claims in client copy.</p>
        </article>
      </div>
    </section>
  );
}
